import { createCRUDHook } from './createCRUDHook';
import {
  AssemblyFilterOptionsModel,
  PartAssemblyItem,
  PartAssemblyList,
  PartAssemblyWithItems,
  partAssemblyChangeLogModel,
} from 'utils/models/partAssembly.model';
import {
  assemblyCompleteDetailsDto,
  FilterOptionModel,
  JsonPatch,
  PartAssemblyPDLVersionDto,
} from 'utils/models';
import {
  CHECK_ASSEMBLY_WITH_SAME_PART_QTY_EXIST,
  CHECK_ASSEMBLY_NUMBER_EXIST,
  CLONE_ASSEMBLY_DATA_WITH_ITEMS,
  CREATE_BULK_ENTITY,
  FILTERED_ENTITIES,
  GET_ASSEMBLY_LIST_BY_PART_ID,
  GET_FILTERED_PARTS_ASSEMBLY,
  GET_PART_ASSEMBLY_WITH_ITEMS_BY_ID,
  POST_PART_ASSEMBLY_WITH_ITEMS,
  UPDATE_PART_ASSEMBLY_WITH_ITEMS,
  TBL_PART_ASSEMBLY,
  TBL_PART_ASSEMBLY_CHANGE_LOG,
  TBL_PART_ASSEMBLY_ITEM,
  TBL_PART_ASSEMBLY_ITEM_CHANGE_LOG,
  TBL_PART_ASSEMBLY_OTHER_LINK,
} from './apiEndPoints';
import { prepareQueryParams } from 'utils';
import { AxiosResponse } from 'axios';

interface PartAssemblyCustomOperations {
  getPartAssemblyListData(): Promise<PartAssemblyList>;
  postPartAssemblyData(data: any): Promise<any>;
  deletePartAssemblyDataById(id: number): Promise<string>;
  getPartAssemblyDataById(id: number): Promise<PartAssemblyWithItems>;
  updatePartAssemblyData(data: any): Promise<any>;
  patchUpdateAssemblyData(
    assemblyId: number,
    param: JsonPatch[],
  ): Promise<AxiosResponse>;
  deletePartAssemblyItemById(id: number): Promise<string>;
  deletePartAssemblyOtherLinkById(id: number): Promise<string>;
  checkAssemblyNumberExist(id: string): Promise<boolean>;
  CheckIfAssemblyExistsWithSamePartAndQty(data: any): Promise<any>;
  updatePartAssemblyItemData(
    id: number,
    param: JsonPatch[],
  ): Promise<AxiosResponse>;
  getAssemblyListByPartId(id: number): Promise<PartAssemblyPDLVersionDto[]>;
  getFilteredAssemblyListData(
    filteredEntities: AssemblyFilterOptionsModel,
  ): Promise<PartAssemblyList>;
  postPartAssemblyChangeLogData(
    data: partAssemblyChangeLogModel[],
  ): Promise<any>;
  getPartAssemblyChangeLogDataByPartId(
    filteredEntities: FilterOptionModel,
  ): Promise<any>;
  getPartAssemblyItemChangeLogDataByPartId(
    filteredEntities: FilterOptionModel,
  ): Promise<any>;
  getAssemblyItemsByFE(
    filteredEntities: FilterOptionModel,
  ): Promise<PartAssemblyItem[]>;
  clonePartAssemblyDataById(id: number): Promise<any>;
  getAssemblyCompleteDetails(id: number): Promise<assemblyCompleteDetailsDto>;
}

const { Context, useManager, useConsumer } = createCRUDHook<
  PartAssemblyList,
  PartAssemblyList,
  PartAssemblyCustomOperations
>({
  entityName: 'PartAssembly',
  endpoint: TBL_PART_ASSEMBLY,

  customOperations: (auth) => ({
    getPartAssemblyListData: async (): Promise<PartAssemblyList> => {
      try {
        let res = await auth.makeAuthenticatedGet(TBL_PART_ASSEMBLY);
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    postPartAssemblyData: async (data: any): Promise<any> => {
      try {
        let res = await auth.makeAuthenticatedPost(
          TBL_PART_ASSEMBLY + POST_PART_ASSEMBLY_WITH_ITEMS,
          data,
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    deletePartAssemblyDataById: async (id: number): Promise<string> => {
      try {
        let res = await auth.makeAuthenticatedDelete(
          TBL_PART_ASSEMBLY + `/${id}`,
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    getPartAssemblyDataById: async (
      id: number,
    ): Promise<PartAssemblyWithItems> => {
      try {
        let res = await auth.makeAuthenticatedGet(
          `${TBL_PART_ASSEMBLY}${GET_PART_ASSEMBLY_WITH_ITEMS_BY_ID}?assemblyId=${id}`,
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    updatePartAssemblyData: async (params: any): Promise<any> => {
      try {
        let res = await auth.makeAuthenticatedPut(
          TBL_PART_ASSEMBLY + UPDATE_PART_ASSEMBLY_WITH_ITEMS,
          params,
        );
        return Promise.resolve(res);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    patchUpdateAssemblyData: async (
      assemblyId: number,
      param: JsonPatch[],
    ): Promise<AxiosResponse> => {
      try {
        let res = await auth.makeAuthenticatedPatch(
          TBL_PART_ASSEMBLY + `/${assemblyId}`,
          param,
        );
        return Promise.resolve(res);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    deletePartAssemblyItemById: async (id: number): Promise<string> => {
      try {
        let res = await auth.makeAuthenticatedDelete(
          TBL_PART_ASSEMBLY_ITEM + `/${id}`,
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    deletePartAssemblyOtherLinkById: async (id: number): Promise<string> => {
      try {
        let res = await auth.makeAuthenticatedDelete(
          TBL_PART_ASSEMBLY_OTHER_LINK + `/${id}`,
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    checkAssemblyNumberExist: async (id: string): Promise<boolean> => {
      try {
        let res = await auth.makeAuthenticatedGet(
          TBL_PART_ASSEMBLY +
            CHECK_ASSEMBLY_NUMBER_EXIST +
            `?assemblyNumber=${id}`,
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    CheckIfAssemblyExistsWithSamePartAndQty: async (
      data: any,
    ): Promise<any> => {
      try {
        let res = await auth.makeAuthenticatedPost(
          TBL_PART_ASSEMBLY_ITEM + CHECK_ASSEMBLY_WITH_SAME_PART_QTY_EXIST,
          data,
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    updatePartAssemblyItemData: async (
      id: number,
      param: JsonPatch[],
    ): Promise<AxiosResponse> => {
      try {
        let res = await auth.makeAuthenticatedPatch(
          TBL_PART_ASSEMBLY_ITEM + `/${id}`,
          param,
        );
        return Promise.resolve(res);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    getAssemblyListByPartId: async (
      id: number,
    ): Promise<PartAssemblyPDLVersionDto[]> => {
      try {
        let res = await auth.makeAuthenticatedGet(
          TBL_PART_ASSEMBLY + GET_ASSEMBLY_LIST_BY_PART_ID + `?partId=${id}`,
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    getFilteredAssemblyListData: async (
      filteredEntities: AssemblyFilterOptionsModel,
    ): Promise<PartAssemblyList> => {
      try {
        let res = await auth.makeAuthenticatedGet(
          TBL_PART_ASSEMBLY +
            GET_FILTERED_PARTS_ASSEMBLY +
            `?${prepareQueryParams(filteredEntities)}`,
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    postPartAssemblyChangeLogData: async (
      data: partAssemblyChangeLogModel[],
    ): Promise<any> => {
      try {
        let res = await auth.makeAuthenticatedPost(
          TBL_PART_ASSEMBLY_CHANGE_LOG + CREATE_BULK_ENTITY,
          data,
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    getPartAssemblyChangeLogDataByPartId: async (
      filteredEntities: FilterOptionModel,
    ): Promise<any> => {
      try {
        let res = await auth.makeAuthenticatedGet(
          TBL_PART_ASSEMBLY_CHANGE_LOG +
            FILTERED_ENTITIES +
            `?${prepareQueryParams(filteredEntities)}`,
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    getPartAssemblyItemChangeLogDataByPartId: async (
      filteredEntities: FilterOptionModel,
    ): Promise<any> => {
      try {
        let res = await auth.makeAuthenticatedGet(
          TBL_PART_ASSEMBLY_ITEM_CHANGE_LOG +
            FILTERED_ENTITIES +
            `?${prepareQueryParams(filteredEntities)}`,
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    getAssemblyItemsByFE: async (
      filteredEntities: FilterOptionModel,
    ): Promise<PartAssemblyItem[]> => {
      try {
        let res = await auth.makeAuthenticatedGet(
          TBL_PART_ASSEMBLY_ITEM +
            FILTERED_ENTITIES +
            `?${prepareQueryParams(filteredEntities)}`,
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    clonePartAssemblyDataById: async (id: number): Promise<any> => {
      try {
        let res = await auth.makeAuthenticatedGet(
          TBL_PART_ASSEMBLY +
            CLONE_ASSEMBLY_DATA_WITH_ITEMS +
            `?assemblyId=${id}`,
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    getAssemblyCompleteDetails: async (
      id: number,
    ): Promise<assemblyCompleteDetailsDto> => {
      try {
        let res = await auth.makeAuthenticatedGet(
          TBL_PART_ASSEMBLY + `/GetAssemblyCompleteDetails?assemblyId=${id}`,
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
  }),
});

export const PartAssemblyContext = Context;
export const usePartAssemblyManager = useManager;
export const usePartAssembly = useConsumer;
